import { datadogLogs } from "@datadog/browser-logs";
import type { ClientFetchOption } from "better-auth";

import { IS_CAPACITOR, IS_CHROME_EXTENSION, IS_IN_IFRAME } from "@src/env";

import {
  BEARER_TOKEN_KEY,
  DONT_REMEMBER_REQUEST_HEADER,
  DONT_REMEMBER_RESPONSE_HEADER,
  TWO_FACTOR_CHALLENGE_KEY,
  TWO_FACTOR_CHALLENGE_REQUEST_HEADER,
  TWO_FACTOR_CHALLENGE_RESPONSE_HEADER,
} from "./auth.constants";

/**
 * Central setter for the bearer token, mirroring {@link getBearerAuthToken}.
 * Every path that mints a token for the bearer-auth embeddings (chrome
 * extension, iframe, Capacitor) should write it through here rather than
 * touching `localStorage` directly.
 */
export function setBearerAuthToken(token: string): void {
  if (!token || !token.trim()) {
    datadogLogs.logger.error("Refused to store empty bearer token");
    return;
  }

  localStorage.setItem(BEARER_TOKEN_KEY, token);
}

function persistAuthTokenFromResponse(
  ctx: Parameters<NonNullable<ClientFetchOption["onSuccess"]>>[0],
) {
  const authToken = ctx.response.headers.get("set-auth-token");

  if (authToken) {
    setBearerAuthToken(authToken);
  }
}

/**
 * Mirrors better-auth's `twoFactorCookieMaxAge` default. The server stays
 * authoritative — this only stops us from sending a header we already know is
 * dead, so an abandoned challenge heals itself instead of lingering.
 */
const TWO_FACTOR_CHALLENGE_TTL_MS = 600_000;

/**
 * An in-flight challenge. Written and read as one value so a partial state — a
 * token with no expiry, say — can't be observed.
 */
type StoredTwoFactorChallenge = {
  token: string;
  expiresAt: number;
  dontRemember: string | null;
};

function readTwoFactorChallenge(): StoredTwoFactorChallenge | null {
  const raw = localStorage.getItem(TWO_FACTOR_CHALLENGE_KEY);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredTwoFactorChallenge>;

    if (!parsed.token || typeof parsed.expiresAt !== "number") return null;

    return {
      token: parsed.token,
      expiresAt: parsed.expiresAt,
      dontRemember: parsed.dontRemember ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * A 2FA-enrolled sign-in answers with a challenge instead of a session. The
 * server's `twoFactorBearerChallenge` plugin mirrors the challenge cookies into
 * these headers for us, because cookies don't round-trip on the bearer surfaces.
 *
 * A stored challenge and a session are mutually exclusive: the challenge exists
 * precisely because no session was issued, so any response that *does* establish
 * a session ends the challenge. The server's before-hook already yields to a real
 * bearer session on `/two-factor/*` calls (`hasBearerSession`), so this is
 * belt-and-braces rather than the only thing standing between a stale token and a
 * broken enrolment.
 */
function persistTwoFactorChallengeFromResponse(
  ctx: Parameters<NonNullable<ClientFetchOption["onSuccess"]>>[0],
) {
  const token = ctx.response.headers.get(TWO_FACTOR_CHALLENGE_RESPONSE_HEADER);

  if (token) {
    // A challenge means sign-in issued no session, so whatever bearer token is
    // still lying around is dead. Dropping it keeps "has a bearer token" a
    // reliable signal of "is signed in", which is what the server's before-hook
    // uses to tell a live challenge from a stale one.
    localStorage.removeItem(BEARER_TOKEN_KEY);
    localStorage.setItem(
      TWO_FACTOR_CHALLENGE_KEY,
      JSON.stringify({
        token,
        expiresAt: Date.now() + TWO_FACTOR_CHALLENGE_TTL_MS,
        dontRemember: ctx.response.headers.get(DONT_REMEMBER_RESPONSE_HEADER),
      } satisfies StoredTwoFactorChallenge),
    );

    return;
  }

  if (ctx.response.headers.get("set-auth-token")) {
    clearTwoFactorChallengeTokens();
  }
}

/**
 * The challenge is single-use and short-lived, so drop it as soon as the flow
 * ends — verified, superseded, expired, or signed out.
 */
export function clearTwoFactorChallengeTokens(): void {
  localStorage.removeItem(TWO_FACTOR_CHALLENGE_KEY);
}

function getTwoFactorChallengeHeaders(): Record<string, string> {
  if (!usesBearerAuth()) return {};

  const challenge = readTwoFactorChallenge();

  if (!challenge) return {};

  if (Date.now() > challenge.expiresAt) {
    clearTwoFactorChallengeTokens();
    return {};
  }

  return {
    [TWO_FACTOR_CHALLENGE_REQUEST_HEADER]: challenge.token,
    ...(challenge.dontRemember
      ? { [DONT_REMEMBER_REQUEST_HEADER]: challenge.dontRemember }
      : {}),
  };
}

export function usesBearerAuth(): boolean {
  return IS_CHROME_EXTENSION || IS_IN_IFRAME || IS_CAPACITOR;
}

export function getBearerAuthToken(): string | null {
  if (!usesBearerAuth()) {
    return null;
  }

  return localStorage.getItem(BEARER_TOKEN_KEY);
}

export async function getBearerAuthHeaders(): Promise<Record<
  string,
  string
> | null> {
  if (!usesBearerAuth()) {
    return {};
  }

  const token = getBearerAuthToken();

  if (token) {
    return { Authorization: `Bearer ${token}` };
  }

  return null;
}

const bearerFetchOptions: ClientFetchOption = {
  onSuccess: (ctx) => {
    persistAuthTokenFromResponse(ctx);
    persistTwoFactorChallengeFromResponse(ctx);
  },
  onRequest: (ctx) => {
    // Only the two-factor endpoints can act on a challenge, so don't broadcast
    // it across every authenticated call.
    if (!String(ctx.url).includes("/two-factor/")) return ctx;

    for (const [header, value] of Object.entries(
      getTwoFactorChallengeHeaders(),
    )) {
      ctx.headers.set(header, value);
    }

    return ctx;
  },
  auth: {
    type: "Bearer",
    token: async () => {
      return localStorage.getItem(BEARER_TOKEN_KEY) || "";
    },
  },
};

const defaultFetchOptions: ClientFetchOption = {
  credentials: "include" as const,
};

export function getAuthClientFetchOptions(): ClientFetchOption {
  return usesBearerAuth() ? bearerFetchOptions : defaultFetchOptions;
}
